import React from 'react';
import { cn } from '@/lib/utils';
import type { GraphRunSummary } from './useGraphRuns';

function formatRelativeTime(dateString: string): string {
    const now = Date.now();
    const then = new Date(dateString).getTime();
    const seconds = Math.floor((now - then) / 1000);

    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return new Date(dateString).toLocaleDateString();
}

function formatExactTime(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        fractionalSecondDigits: 3,
    });
}

function formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    const seconds = (ms / 1000).toFixed(1);
    return `${seconds}s`;
}

interface Props {
    runsQuery: {
        data?: { pages: { runs: GraphRunSummary[]; nextCursor: string | null }[] };
        isLoading: boolean;
        hasNextPage?: boolean;
        isFetchingNextPage: boolean;
        fetchNextPage: () => void;
    };
    onClose: () => void;
    onSelectRun: (runId: string) => void;
    activeRunId?: string | null;
    livePreviewEnabled: boolean;
    onToggleLivePreview: () => void;
}

export const RunHistoryPanel: React.FC<Props> = ({
    runsQuery,
    onClose,
    onSelectRun,
    activeRunId,
    livePreviewEnabled,
    onToggleLivePreview,
}) => {
    const allRuns = runsQuery.data?.pages.flatMap(page => page.runs) ?? [];

    return (
        <div
            className={cn(
                'absolute top-4 right-4 w-[380px] h-[70%]',
                'flex flex-col z-[1000] pointer-events-auto overflow-hidden',
                'rounded-2xl border shadow-xl',
                'bg-white/80 dark:bg-neutral-900/80',
                'backdrop-blur-xl',
                'border-white/30 dark:border-neutral-700/50',
                'animate-in slide-in-from-right-5 fade-in duration-200',
            )}
        >
            {/* Header */}
            <div
                className={cn(
                    'flex items-center justify-between px-4 py-3.5',
                    'border-b rounded-t-2xl',
                    'bg-white/50 dark:bg-neutral-800/50',
                    'backdrop-blur-sm',
                    'border-white/20 dark:border-neutral-700/30',
                )}
            >
                <h2 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">Run History</h2>
                <button
                    onClick={onClose}
                    aria-label="Close"
                    className={cn(
                        'p-1 rounded-md transition-colors',
                        'text-neutral-500 dark:text-neutral-400',
                        'hover:bg-neutral-200/50 dark:hover:bg-neutral-700/50',
                        'hover:text-neutral-700 dark:hover:text-neutral-200',
                    )}
                >
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                </button>
            </div>

            {/* Live preview toggle */}
            <button
                onClick={onToggleLivePreview}
                className={cn(
                    'flex items-center gap-2 px-5 py-2.5 w-full text-left transition-colors',
                    'border-b border-white/20 dark:border-neutral-700/30',
                    'hover:bg-neutral-100/40 dark:hover:bg-neutral-800/40',
                )}
            >
                <svg
                    width="14"
                    height="14"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    className={cn(
                        'shrink-0 transition-colors',
                        livePreviewEnabled ? 'text-amber-500 dark:text-amber-400' : 'text-neutral-400 dark:text-neutral-500',
                    )}
                >
                    <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                </svg>
                <span className="text-xs text-neutral-600 dark:text-neutral-400 flex-1">Show live results</span>
                <div
                    className={cn(
                        'w-7 h-4 rounded-full transition-colors relative',
                        livePreviewEnabled ? 'bg-amber-500 dark:bg-amber-400' : 'bg-neutral-300 dark:bg-neutral-600',
                    )}
                >
                    <div
                        className={cn(
                            'absolute top-0.5 w-3 h-3 rounded-full bg-white shadow-sm transition-transform',
                            livePreviewEnabled ? 'translate-x-3.5' : 'translate-x-0.5',
                        )}
                    />
                </div>
            </button>

            {/* Run list */}
            <div className="flex-1 overflow-y-auto">
                {runsQuery.isLoading ? (
                    <div className="flex items-center justify-center py-8 text-sm text-neutral-500 dark:text-neutral-400">
                        Loading runs...
                    </div>
                ) : allRuns.length === 0 ? (
                    <div className="flex items-center justify-center py-8 text-sm text-neutral-500 dark:text-neutral-400">No runs yet</div>
                ) : (
                    <div className="p-2 flex flex-col gap-1">
                        {allRuns.map(run => (
                            <button
                                key={run.id}
                                onClick={() => onSelectRun(run.id)}
                                className={cn(
                                    'w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors',
                                    activeRunId === run.id
                                        ? 'bg-blue-500/15 dark:bg-blue-400/15'
                                        : 'hover:bg-neutral-100/60 dark:hover:bg-neutral-800/60',
                                )}
                            >
                                {/* Status dot */}
                                <div className={cn('w-2.5 h-2.5 rounded-full shrink-0', run.hasError ? 'bg-red-500' : 'bg-emerald-500')} />
                                {/* Timestamp */}
                                <span
                                    className="text-sm text-neutral-700 dark:text-neutral-300 flex-1 truncate"
                                    title={formatExactTime(run.createdAt)}
                                >
                                    {formatRelativeTime(run.createdAt)}
                                </span>
                                {/* Duration badge */}
                                <span
                                    className={cn(
                                        'text-xs px-2 py-0.5 rounded-full shrink-0',
                                        'bg-neutral-200/60 dark:bg-neutral-700/60',
                                        'text-neutral-600 dark:text-neutral-400',
                                    )}
                                >
                                    {formatDuration(run.duration)}
                                </span>
                            </button>
                        ))}

                        {/* Load more */}
                        {runsQuery.hasNextPage && (
                            <button
                                onClick={() => runsQuery.fetchNextPage()}
                                disabled={runsQuery.isFetchingNextPage}
                                className={cn(
                                    'w-full py-2 text-sm text-center rounded-lg transition-colors',
                                    'text-neutral-500 dark:text-neutral-400',
                                    'hover:bg-neutral-100/60 dark:hover:bg-neutral-800/60',
                                )}
                            >
                                {runsQuery.isFetchingNextPage ? 'Loading...' : 'Load more'}
                            </button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};
